// shelbulaworkspaces/WorkspaceSettingsModal.jsx

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader } from 'lucide-react';
import { apiManager } from './apiManager';
import { authManager } from '@/managers/AuthManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { doesWorkspaceNameConflict, sanitizeWorkspaceName } from '@/utils/workspaceNameUtils';

const WorkspaceSettingsModal = ({ isOpen, onClose, workspace, onInitiateDelete, onUpdateSuccess }) => {
    const [isMounted, setIsMounted] = useState(false);
    const [friendlyName, setFriendlyName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [authState, setAuthState] = useState(authManager.getState());

    useEffect(() => {
      const unsubscribe = authManager.subscribe(setAuthState);
      return () => unsubscribe();
    }, []);
    
    useEffect(() => {
        setIsMounted(true);
        if (workspace) {
            setFriendlyName(workspace.workspaceName || '');
            setError(null); // Reset error when a new workspace is loaded
        }
    }, [workspace]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const handleSaveName = async () => {
        const nextWorkspaceName = sanitizeWorkspaceName(friendlyName);
        const currentWorkspaceName = sanitizeWorkspaceName(workspace?.workspaceName || '');

        if (!workspace || workspace.isDefault || !authState.user || isSaving || !nextWorkspaceName || nextWorkspaceName === currentWorkspaceName) {
            return;
        }

        const existingWorkspaces = workspaceManager.getState().workspaces || [];
        if (doesWorkspaceNameConflict(existingWorkspaces, nextWorkspaceName, workspace.id)) {
            setError(`A workspace named '${nextWorkspaceName}' already exists.`);
            return;
        }
        
        setIsSaving(true);
        setError(null);
        
        try {
            const result = await apiManager.updateWorkspaceDetails(
                authState.user.uid,
                workspace.id,
                { workspaceName: nextWorkspaceName }
            );
            if (result.success && result.updatedWorkspace) {
                onUpdateSuccess(result.updatedWorkspace);
                onClose();
            }
        } catch (err) {
            console.error("Failed to save workspace name:", err);
            setError(err.message || "An unknown error occurred.");
        } finally {
            setIsSaving(false);
        }
    };

    const displayName = workspace?.isDefault ? 'Main Workspace' : (workspace?.workspaceName || '');
    const deleteButtonLabel = workspace?.isDefault ? 'Delete Workspace' : 'Delete Workspace';
    const deleteHelpText = workspace?.isDefault
        ? 'Deleting the main workspace clears all of its files and resets it back to a fresh main workspace.'
        : 'Deleting this workspace permanently removes its files and settings.';
    
    if (!isMounted || !workspace) return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="relative w-full max-w-md bg-surface rounded-lg border border-border-color shadow-xl"
                    >
                        <div className="flex justify-between items-center p-4 border-b border-border-color">
                            <h2 className="text-lg font-bold text-on-surface">Workspace Settings</h2>
                            <button onClick={onClose} className="p-1 rounded-full text-on-surface-dim hover:bg-surface-dark hover:text-on-surface">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6">
                            <div className="space-y-2 mb-6">
                                <label htmlFor="friendlyName" className="text-sm font-medium text-on-surface">Workspace Name</label>
                                 <p className="text-xs text-on-surface-dim -mt-1 mb-2">{workspace.isDefault ? 'This is your Main Workspace. It cannot be renamed, but it can be cleared and reset.' : 'This is the friendly name for your workspace. Duplicate names are not allowed.'}</p>
                                <input
                                    id="friendlyName"
                                    type="text"
                                    value={workspace.isDefault ? displayName : friendlyName}
                                    onChange={(e) => setFriendlyName(e.target.value)}
                                    disabled={workspace.isDefault}
                                    className="w-full px-3 py-2 bg-background-dark border border-border-color rounded-md text-on-surface focus:ring-2 focus:ring-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                                />
                                <p className="text-xs text-on-surface-dim">ID: <span className="font-mono">{workspace.id.length > 20 ? `${workspace.id.slice(0, 18)}…` : workspace.id}</span></p>
                            </div>
                            
                            {error && (
                                <p className="text-xs text-red-400 bg-red-900/50 p-2 rounded-md mb-4">{error}</p>
                            )}

                            <div className="mb-6 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                                <div className="space-y-1.5">
                                    <h3 className="text-sm font-medium text-on-surface">Delete Workspace</h3>
                                    <p className="text-xs text-on-surface-dim">{deleteHelpText}</p>
                                    <button
                                        type="button"
                                        onClick={onInitiateDelete}
                                        className="text-sm text-red-500 hover:text-red-400 hover:underline"
                                    >
                                        {deleteButtonLabel}
                                    </button>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-border-color/50">
                                 <div />
                                 <button 
                                     onClick={handleSaveName} 
                                     disabled={workspace.isDefault || isSaving || !sanitizeWorkspaceName(friendlyName) || sanitizeWorkspaceName(friendlyName) === sanitizeWorkspaceName(workspace.workspaceName || '')}
                                     className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark transition-colors disabled:bg-primary/50 disabled:cursor-not-allowed flex items-center gap-2"
                                 >
                                    {isSaving && <Loader size={16} className="animate-spin" />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    , document.body);
};

export default WorkspaceSettingsModal;